import React, { useState, useEffect, useMemo } from "react";
import { Divider, VStack, useDisclosure } from "@chakra-ui/react";

// Import types
import { driverBoardTypes, recruitersTypes } from "../../../types/Types";

// Import components
import ToolBar from "./all/ToolBar";
import AllDataTable from "./all/AllDataTable";
import NewForm from "./all/NewForm";
import EditForm from "../shared/EditForm";

// Import hooks
import useFetchProfileData from "../../../hooks/driverboard/fetchProfiledata";
import useFetchHistory from "../../../hooks/driverboard/fetchHistory";

interface props {
  userName: string;
  userEmail: string;
  userDepartment: string;
  searchText: string;
  selectedView: "All" | "Personal";
  data: driverBoardTypes[];
  isLoading: boolean;
  lastUpdated: Date | null;
  refresh: boolean;
  sortState: {
    column: keyof driverBoardTypes | null;
    direction: "asc" | "desc";
  };
  setSelectedView: (value: "All" | "Personal") => void;
  setSearchText: (value: string) => void;
  setRefresh: (value: boolean) => void;
  setSortState: (value: {
    column: keyof driverBoardTypes | null;
    direction: "asc" | "desc";
  }) => void;
  subTeam: "Tasha" | "Austin" | "All" | "Kia";
  setSubTeam: (value: "Tasha" | "Austin" | "All" | "Kia") => void;
  recruiters: recruitersTypes[];
}

const AllView: React.FC<props> = ({
  userName,
  userEmail,
  userDepartment,
  searchText,
  selectedView,
  data,
  isLoading,
  lastUpdated,
  refresh,
  sortState,
  setSelectedView,
  setSearchText,
  setRefresh,
  setSortState,
  subTeam,
  setSubTeam,
  recruiters,
}) => {
  const [selectedID, setSelectedID] = useState<number | null>(null);
  const [manualRefresh, setManualRefresh] = useState<boolean>(false);
  const [statusView, setStatusView] = useState<
    | "All"
    | "New"
    | "Scheduled"
    | "Showed"
    | "No Show"
    | "TDO"
    | "Seated"
    | "Upgraded"
    | "Withdrawn"
  >("All");
  const [selectedRegistration, setSelectedRegistration] =
    useState<string>("All");

  // Fetch Versioning History
  const { historyLoading, changeLog } = useFetchHistory(
    selectedID,
    manualRefresh
  );

  // Fetch Profile
  const { profileLoading, profileData } = useFetchProfileData(
    selectedID,
    manualRefresh
  );

  // Function to refresh profile data manually
  const refreshProfileData = () => {
    setManualRefresh(!manualRefresh);
  };

  // New Form State
  const {
    isOpen: isNewFormOpen,
    onOpen: onNewFormOpen,
    onClose: onNewFormClose,
  } = useDisclosure();

  // Edit Form State
  const {
    isOpen: isEditFormOpen,
    onOpen: onEditFormOpen,
    onClose: onEditFormClose,
  } = useDisclosure();

  // Function to handle row selection
  const handleSelect = (id: number) => {
    setSelectedID(id);
    onEditFormOpen();
  };

  // Function to close the edit form
  const handleEditFormClose = () => {
    onEditFormClose();
    setSelectedID(null);
  };

  // Filter specific data based on statusView, subTeam, and selectedRegistration
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // View Status
      const statusMatch = statusView === "All" || item.Status === statusView;

      // Sub Team
      const subTeamMatch = subTeam === "All" || item.Subteam === subTeam;

      // Registration Type
      const registrationMatch =
        selectedRegistration === "All" ||
        (selectedRegistration === "All Trainees" &&
          (item.Registration === "2 Weeks Trainee" ||
            item.Registration === "4 Weeks Trainee")) ||
        item.Registration === selectedRegistration;

      return statusMatch && subTeamMatch && registrationMatch;
    });
  }, [statusView, selectedRegistration, subTeam, data]);

  useEffect(() => {
    document.title = "Driver Board - Pipeline";
  }, []);

  return (
    <>
      <VStack
        width="calc(100% - 200px)"
        height="100%"
        background="white"
        shadow="md"
        borderRadius="2xl"
        padding="10px"
      >
        <ToolBar
          selectedView={selectedView}
          searchText={searchText}
          data={filteredData}
          isLoading={isLoading}
          lastUpdated={lastUpdated}
          setSelectedView={setSelectedView}
          setSearchText={setSearchText}
          onNewFormOpen={onNewFormOpen}
          onRefresh={() => setRefresh(!refresh)}
          setStatusView={setStatusView}
          statusView={statusView}
          subTeam={subTeam}
          setSubTeam={setSubTeam}
          selectedRegistration={selectedRegistration}
          setSelectedRegistration={setSelectedRegistration}
        />
        <Divider />
        <AllDataTable
          data={filteredData}
          selectedID={selectedID}
          sortState={sortState}
          handleSelect={handleSelect}
          handleSort={(column) => {
            if (sortState.column === column) {
              setSortState({
                column,
                direction: sortState.direction === "asc" ? "desc" : "asc",
              });
            } else {
              setSortState({ column, direction: "asc" });
            }
          }}
        />
      </VStack>
      <NewForm
        isNewFormOpen={isNewFormOpen}
        onNewFormClose={onNewFormClose}
        userEmail={userEmail}
        onRefresh={() => setRefresh(!refresh)}
        userName={userName}
        userDepartment={userDepartment}
        recruiters={recruiters}
      />
      <EditForm
        userName={userName}
        userEmail={userEmail}
        userDepartment={userDepartment}
        isOpen={isEditFormOpen}
        onClose={handleEditFormClose}
        onRefresh={() => setRefresh(!refresh)}
        selectedID={selectedID}
        profileLoading={profileLoading}
        profileData={profileData}
        refreshProfileData={refreshProfileData}
        setSelectedID={setSelectedID}
        historyLoading={historyLoading}
        changeLog={changeLog}
      />
    </>
  );
};

export default AllView;
